/**
 * Low-level crypto primitives.
 *
 * Everything here is pure JavaScript (@noble/*) with no native bindings, so the
 * exact same code runs on Node, in a browser and under React Native's Hermes.
 * That is deliberate: the ratchet state on a phone and on the web must agree
 * byte-for-byte, and a native module that exists on only one of them would make
 * that impossible to guarantee.
 */
import { ed25519, x25519, edwardsToMontgomeryPriv, edwardsToMontgomeryPub } from '@noble/curves/ed25519';
import { sha256 } from '@noble/hashes/sha2';
import { hmac } from '@noble/hashes/hmac';
import { hkdf } from '@noble/hashes/hkdf';
import { randomBytes, concatBytes } from '@noble/hashes/utils';
import { xchacha20poly1305 } from '@noble/ciphers/chacha';

export { randomBytes, concatBytes, sha256, hmac, hkdf };

export const KEY_LEN = 32;
export const NONCE_LEN = 24;

// ---------------------------------------------------------------------------
// Encoding
// ---------------------------------------------------------------------------

const B64_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

/** base64 encode. Implemented by hand so we don't depend on Buffer or atob. */
export function toB64(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i];
    const b1 = bytes[i + 1];
    const b2 = bytes[i + 2];
    out += B64_CHARS[b0 >> 2];
    out += B64_CHARS[((b0 & 3) << 4) | ((b1 ?? 0) >> 4)];
    out += b1 === undefined ? '=' : B64_CHARS[((b1 & 15) << 2) | ((b2 ?? 0) >> 6)];
    out += b2 === undefined ? '=' : B64_CHARS[b2 & 63];
  }
  return out;
}

const B64_LOOKUP = /* @__PURE__ */ (() => {
  const table = new Uint8Array(256).fill(255);
  for (let i = 0; i < B64_CHARS.length; i++) table[B64_CHARS.charCodeAt(i)] = i;
  return table;
})();

/** base64 decode. Throws on malformed input rather than silently truncating. */
export function fromB64(s: string): Uint8Array {
  let end = s.length;
  while (end > 0 && s[end - 1] === '=') end--;
  const out = new Uint8Array(Math.floor((end * 3) / 4));
  let acc = 0;
  let accBits = 0;
  let o = 0;
  for (let i = 0; i < end; i++) {
    const v = B64_LOOKUP[s.charCodeAt(i)];
    if (v === 255) throw new Error('invalid base64 input');
    acc = (acc << 6) | v;
    accBits += 6;
    if (accBits >= 8) {
      accBits -= 8;
      out[o++] = (acc >> accBits) & 0xff;
    }
  }
  return out.subarray(0, o);
}

const utf8Encoder = new TextEncoder();
const utf8Decoder = new TextDecoder();

export const utf8 = (s: string): Uint8Array => utf8Encoder.encode(s);
export const fromUtf8 = (b: Uint8Array): string => utf8Decoder.decode(b);

export function toHex(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

/**
 * Constant-time equality. Used wherever an attacker controls one side of the
 * comparison (auth tags, safety numbers, media digests) so that timing does not
 * leak how many leading bytes were correct.
 */
export function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
  return diff === 0;
}

// ---------------------------------------------------------------------------
// Key agreement and signatures
// ---------------------------------------------------------------------------

export interface KeyPair {
  /** 32-byte private scalar/seed. Never leaves the device. */
  priv: Uint8Array;
  pub: Uint8Array;
}

/** Fresh X25519 keypair, used for ephemeral, prekey and ratchet keys. */
export function generateDHKeyPair(): KeyPair {
  const priv = x25519.utils.randomPrivateKey();
  return { priv, pub: x25519.getPublicKey(priv) };
}

/** Long-term Ed25519 identity keypair. Signs prekeys and login challenges. */
export function generateIdentityKeyPair(): KeyPair {
  const priv = ed25519.utils.randomPrivateKey();
  return { priv, pub: ed25519.getPublicKey(priv) };
}

/**
 * Reuse the Ed25519 identity key for Diffie-Hellman by converting it to its
 * Montgomery (X25519) form. This is the standard birational map Signal relies
 * on for XEdDSA, and it means a user has exactly one long-term identity to
 * verify rather than two that could drift apart.
 */
export function identityToDH(idKeys: KeyPair): KeyPair {
  return {
    priv: edwardsToMontgomeryPriv(idKeys.priv),
    pub: edwardsToMontgomeryPub(idKeys.pub),
  };
}

export function identityPubToDH(edPub: Uint8Array): Uint8Array {
  return edwardsToMontgomeryPub(edPub);
}

export function sign(priv: Uint8Array, message: Uint8Array): Uint8Array {
  return ed25519.sign(message, priv);
}

export function verify(pub: Uint8Array, message: Uint8Array, signature: Uint8Array): boolean {
  try {
    return ed25519.verify(signature, message, pub);
  } catch {
    // A malformed signature or off-curve point is a verification failure, not a
    // crash — callers treat this as "reject the message".
    return false;
  }
}

/**
 * X25519 shared secret.
 *
 * noble rejects the low-order points that would force an all-zero output, so a
 * peer cannot pin the shared secret to a known value by sending a crafted
 * public key; that throw propagates as a failed handshake.
 */
export function dh(priv: Uint8Array, pub: Uint8Array): Uint8Array {
  return x25519.getSharedSecret(priv, pub);
}

// ---------------------------------------------------------------------------
// KDFs
// ---------------------------------------------------------------------------

export function kdf(ikm: Uint8Array, salt: Uint8Array, info: string, length: number): Uint8Array {
  return hkdf(sha256, ikm, salt, utf8(info), length);
}

// ---------------------------------------------------------------------------
// AEAD
// ---------------------------------------------------------------------------

/**
 * XChaCha20-Poly1305. The 24-byte nonce is large enough to be derived
 * deterministically from a message key without birthday-bound worries, which
 * lets every message key be used exactly once with a fixed nonce.
 */
export function aeadEncrypt(
  key: Uint8Array,
  nonce: Uint8Array,
  plaintext: Uint8Array,
  aad?: Uint8Array,
): Uint8Array {
  return xchacha20poly1305(key, nonce, aad).encrypt(plaintext);
}

/** Throws if the tag does not verify — i.e. on tampering or a wrong key. */
export function aeadDecrypt(
  key: Uint8Array,
  nonce: Uint8Array,
  ciphertext: Uint8Array,
  aad?: Uint8Array,
): Uint8Array {
  return xchacha20poly1305(key, nonce, aad).decrypt(ciphertext);
}
